import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './Contexts/AuthContext';
import AllowAccessTo from './Services/AllowAccessTo';
import AuthGuard from './Services/AuthGuard';

// Pages
import App from './App';
import NoMatch from './Pages/NoMatch';

import InstagramConnectionContent from './Pages/InstagramConnectionPage';

import AgentsHub from './Pages/AgentsHub';
import SimulationDatasetHub from './Pages/SimulationDatasetHub';
import SimulationDatasetDetail from './Pages/SimulationDatasetDetail';
import SimulationDatasetConversationDetail from './Pages/SimulationDatasetConversationDetail';
import SimulationDatasetEvaluationDetail from './Pages/SimulationDatasetEvaluationDetail';
import SimulationDatasetScanDetail from './Pages/SimulationDatasetScanDetail';
import Channels from './Pages/Channels';
import CompanySettings from './Pages/CompanySettings';
import DataIntHub from './Pages/DataIntHub';
import DataStorage from './Pages/DataStorage';
import DocumentDetails from './Pages/DocumentDetails';
import DocumentsHub from './Pages/DocumentsHub';
import ListOfComponents from './Pages/ListOfComponents';
import MetricsHub from './Pages/MetricsHub';
import NewDocument from './Pages/NewDocument';
import NewOrg from './Pages/NewOrg';
import Organization from './Pages/Organization';
import Organizations from './Pages/Organizations';
import OrgSettings from './Pages/OrgSettings';
import Prompt from './Pages/Prompt';
import PromptsHub from './Pages/PromptsHub';
import UserAccount from './Pages/UserAccount';
import UsersHub from './Pages/UsersHub';

import Agent from './Pages/Agent';
import AgentFrame from './Pages/AgentFrame';
import AgentFrames from './Pages/AgentFrames';
import Channel from './Pages/Channel';
import Marker from './Pages/Marker';
import Markers from './Pages/Markers';
import Notification from './Pages/Notification';

import ChatMessages from './Components/Chat/ChatMessages';
import Chats from './Pages/Chats';
import NotificationsHub from './Pages/NotificationsHub';

import AgentAuditOperationPage from './Pages/AgentAuditOperation';
import AgentAuditOperationsList from './Pages/AgentAuditOperationsList';
import AgentInstructions from './Pages/AgentInstractions';
import ApiKey from './Pages/ApiKey';
import ApiKeyHub from './Pages/ApiKeyHub';
import ArtifactBatchCreate from './Pages/ArtifactBatchCreate';
import ArtifactDetail from './Pages/ArtifactDetail';
import ArtifactHub from './Pages/ArtifactHub';
import AuditOperationDetail from './Pages/AuditOperationDetail';
import AuditOperationsHub from './Pages/AuditOperationsHub';
import LlmRequestDetail from './Pages/LlmRequestDetail';
import ConnectionDetail from './Pages/ConnectionDetail';
import ConnectionObjectDetail from './Pages/ConnectionObjectDetail';
import Connections from './Pages/Connections';
import ConnectionsObjectMap from './Pages/ConnectionsObjectMap';
import ConnectionsObjectMapDetail from './Pages/ConnectionsObjectMapDetail';
import DictionaryTermDetail from './Pages/DictionaryTermDetail';
import DictionaryTerms from './Pages/DictionaryTerms';
import DocumentCollection from './Pages/DocumentCollection';
import DocumentCollectionHub from './Pages/DocumentCollectionHub';
import EditProductCatalog from './Pages/EditProductCatalog';
import FragmentDetail from './Pages/FragmentDetail';
import FragmentHub from './Pages/FragmentHub';
import GoogleDriveConnectionPage from './Pages/GoogleDriveConnectionPage';
import ZohoConnectionCallbackPage from './Pages/ZohoConnectionCallbackPage';
import InformationPackage from './Pages/InformationPackage';
import InformationPackageArtifact from './Pages/InformationPackageArtifact';
import InformationPackageCollections from './Pages/InformationPackageCollections';
import InformationPackageDetail from './Pages/InformationPackageDetail';
import InformationPackageUnit from './Pages/InformationPackageUnit';
import NewInformationPackage from './Pages/NewInformationPackage';
import VoiceProfiles from './Pages/VoiceProfiles';
import VoiceProfileDetails from './Pages/VoiceProfileDetails';
import NewVoiceProfile from './Pages/NewVoiceProfile';
import NewProductCatalog from './Pages/NewProductCatalog';
import NewTaskSchedule from './Pages/NewTaskSchedule';
import AiKeyDetail from './Pages/AiKeyDetail';
import AiKeysHub from './Pages/AiKeysHub';
import OrganizationDetail from './Pages/OrganizationDetail';
import PostCallAnalysisDetail from './Pages/PostCallAnalysisDetail';
import PostCallAnalysisHub from './Pages/PostCallAnalysisHub';
import PostCallAnalytics from './Pages/PostCallAnalytics';
import ProductCatalogs from './Pages/ProductCatalogs';
import RecordRouteDetail from './Pages/RecordRouteDetail';
import RecordRouteHub from './Pages/RecordRouteHub';
import TaskSchedule from './Pages/TaskSchedule';
import TaskScheduleDetails from './Pages/TaskScheduleDetails';
import TaskScheduleExecutionAuditOperation from './Pages/TaskScheduleExecutionAuditOperation';
import TaskScheduleExecutionAuditOperations from './Pages/TaskScheduleExecutionAuditOperations';
import TaxonomyDictionary from './Pages/TaxonomyDictionary';
import TaxonomyDictionaryHub from './Pages/TaxonomyDictionaryHub';
import ThematicsListPage from './Pages/Thematics';
import Thematic from './Pages/ThematicUpdate';
import User from './Pages/User';
import CONFIG from './Services/CONFIG';
import InformationPackageCollection from './Pages/InformationPackageCollection';

const Router = () => {
  const { status } = useAuth();

  return (
    <BrowserRouter>
      <Routes>
        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <AuthGuard>
              <App />
            </AuthGuard>
          }
        >
          <Route index element={<Navigate to="organizations" replace />} />

          {/* ============================ Components list page ============================ */}
          <Route
            path="components-list"
            element={
              <AllowAccessTo
                permissions={['all:organizations']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <ListOfComponents />
              </AllowAccessTo>
            }
          />

          <Route path="my-account" element={<UserAccount />} />

          {/* ============================ Organization settings ============================ */}
          <Route
            path="organizations/:organizationId/org-settings"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <OrgSettings />
              </AllowAccessTo>
            }
          />

          {/* ============================ New organization creation ============================ */}
          <Route
            path="new-organization"
            element={
              <AllowAccessTo
                permissions={['all:organizations']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <NewOrg />
              </AllowAccessTo>
            }
          />

          {/* ============================ Organizations page ============================ */}
          <Route
            path="organizations"
            element={
              <AllowAccessTo
                permissions={['all:organizations']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <Organizations />
              </AllowAccessTo>
            }
          />

          {/* ============================ Single organization page ============================ */}
          <Route path="organizations/:organizationId" element={<Organization />} />

          {/* ============================ Detail organization page ============================ */}
          <Route path="organizations/:organizationId/detail" element={<OrganizationDetail />} />

          {/* ============================ AI Keys pages ============================ */}
          <Route
            path="organizations/:organizationId/ai-keys"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <AiKeysHub />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/ai-keys/:aiKeyId"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <AiKeyDetail />
              </AllowAccessTo>
            }
          />

          {/* ============================ Prompts pages ============================ */}
          <Route
            path="organizations/:organizationId/prompts-hub"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <PromptsHub />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/prompts-hub/:promptId"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <Prompt canEdit={true} />
              </AllowAccessTo>
            }
          />

          {/* ============================ Simulation Dataset pages ========================== */}
          <Route
            path="organizations/:organizationId/simulation-datasets"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <SimulationDatasetHub />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/simulation-datasets/:datasetKey"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <SimulationDatasetDetail />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/simulation-datasets/:datasetKey/conversations/:conversationKey"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <SimulationDatasetConversationDetail />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/simulation-datasets/:datasetKey/evaluations/:runKey"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <SimulationDatasetEvaluationDetail />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/simulation-datasets/:datasetKey/evaluations/:runKey/tests/:scanKey"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <SimulationDatasetScanDetail />
              </AllowAccessTo>
            }
          />

          {/* ============================ Agents pages ========================== */}
          <Route
            path="organizations/:organizationId/agents-hub"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <AgentsHub />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/agents-hub/:agentId"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <Agent canEdit={true} />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/agents-hub/:agentId"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <Agent canEdit={false} />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/agents-hub/:agentId/audit-operations"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <AgentAuditOperationsList />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/agents-hub/:agentId/audit-operations/:operationId"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <AgentAuditOperationPage />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/agents-hub/:agentId/instructions"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <AgentInstructions canEdit={false} />
              </AllowAccessTo>
            }
          />

          {/* ============================ Agent frames pages ============================ */}
          <Route
            path="organizations/:organizationId/agent-frames"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <AgentFrames canEdit={true} />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/agent-frames/:agentFrameId"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <AgentFrame canEdit={true} />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/agent-frames/:agentFrameId"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <AgentFrame canEdit={false} />
              </AllowAccessTo>
            }
          />

          {/* ============================ Task schedule pages ============================ */}
          <Route
            path="organizations/:organizationId/task-schedule"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <TaskSchedule canEdit={true} />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/task-schedule/new-task-schedule"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <NewTaskSchedule canEdit={true} />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/task-schedule/:taskScheduleId"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <TaskScheduleDetails canEdit={true} />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/task-schedule/:taskScheduleId/executions/:executionId"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <TaskScheduleExecutionAuditOperations />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/task-schedule/:taskScheduleId/executions/:executionId/audit-operations"
            element={
              <Navigate to={`/organizations/${status.organization_id}/task-schedule`} replace />
            }
          />

          <Route
            path="organizations/:organizationId/task-schedule/:taskScheduleId/executions/:executionId/audit-operations/:operationId"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <TaskScheduleExecutionAuditOperation />
              </AllowAccessTo>
            }
          />

          {/* ============================ Markers pages ============================ */}
          <Route
            path="organizations/:organizationId/markers"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <Markers canEdit={true} />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/markers/:markerId"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <Marker canEdit={true} />
              </AllowAccessTo>
            }
          />

          {/* ============================ Data integration pages ============================ */}
          <Route
            path="organizations/:organizationId/data-integration-hub"
            element={
              <AllowAccessTo
                permissions={['all:document']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <DataIntHub />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/data-storage"
            element={
              <AllowAccessTo
                permissions={['all:document']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <DataStorage />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/documents-hub"
            element={
              <AllowAccessTo
                permissions={['all:document']}
                featureFlags={['DOCUMENT']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <DocumentsHub />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/documents-hub/new-document"
            element={
              <AllowAccessTo
                permissions={['all:document']}
                featureFlags={['DOCUMENT']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <NewDocument />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/documents-hub/:documentId"
            element={
              <AllowAccessTo
                permissions={['all:document']}
                featureFlags={['DOCUMENT']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <DocumentDetails />
              </AllowAccessTo>
            }
          />

          {/* ============================ Data integration pages ============================ */}
          <Route
            path="organizations/:organizationId/channels"
            element={
              <AllowAccessTo
                permissions={['all:channel']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <Channels />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/channels/:channelId"
            element={
              <AllowAccessTo
                permissions={['all:channel']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <Channel canEdit={true} />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/channels/:channelId"
            element={
              <AllowAccessTo
                permissions={['all:channel']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <Channel canEdit={false} />
              </AllowAccessTo>
            }
          />

          {/* ============================ Chats page ============================ */}

          <Route
            path="organizations/:organizationId/chats"
            element={
              <AllowAccessTo
                permissions={['all:channel_message']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <Chats />
              </AllowAccessTo>
            }
          >
            {/* Show ChatMessages inside ChatApp on the same page */}
            <Route
              path=":chatId"
              element={
                <AllowAccessTo
                  permissions={['all:channel_message']}
                  redirectTo={`/organizations/${status.organization_id}`}
                >
                  <ChatMessages />
                </AllowAccessTo>
              }
            />
          </Route>

          {/* ============================ Metrics pages ============================ */}
          <Route
            path="organizations/:organizationId/metrics-hub"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <MetricsHub />
              </AllowAccessTo>
            }
          />

          {/* ============================ Users hab pages ============================ */}
          <Route
            path="organizations/:organizationId/users-hub"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <UsersHub canEdit={true} />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/users-hub/:userId"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <User canEdit={true} />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/users-hub/:userId"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <UserAccount />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/company-settings"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <CompanySettings />
              </AllowAccessTo>
            }
          />

          {/* ============================ Products Catalog pages ============================ */}

          <Route
            path="organizations/:organizationId/product-catalogs/:catalogId"
            element={
              <AllowAccessTo
                permissions={['all:document']}
                featureFlags={['PRODUCT']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <EditProductCatalog />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/product-catalogs"
            element={
              <AllowAccessTo
                permissions={['all:document']}
                featureFlags={['PRODUCT']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <ProductCatalogs />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/product-catalogs/new-product-catalog"
            element={
              <AllowAccessTo
                permissions={['all:document']}
                featureFlags={['PRODUCT']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <NewProductCatalog />
              </AllowAccessTo>
            }
          />

          {/* ============================ Taxonomy Dictionary page ============================ */}
          <Route
            path="organizations/:organizationId/taxonomy-dictionary"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <TaxonomyDictionaryHub canEdit={true} />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/taxonomy-dictionary/:dictionaryId"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <TaxonomyDictionary canEdit={true} />
              </AllowAccessTo>
            }
          />

          <Route
            path="/organizations/:organizationId/taxonomy-dictionary/:dictionaryId/terms"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <DictionaryTerms canEdit={true} />
              </AllowAccessTo>
            }
          />

          <Route
            path="/organizations/:organizationId/taxonomy-dictionary/:dictionaryId/terms/:termId"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <DictionaryTermDetail canEdit={true} />
              </AllowAccessTo>
            }
          />

          {/* ============================ Instagram Connection page ============================ */}
          <Route
            path={CONFIG.INST_REDIRECT_PATH}
            element={
              <AllowAccessTo
                permissions={['all:channel']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <InstagramConnectionContent canEdit={false} />
              </AllowAccessTo>
            }
          />

          {/* ============================ Thematic pages ============================ */}
          <Route
            path="organizations/:organizationId/thematic"
            element={
              <AllowAccessTo
                permissions={['all:channel']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <ThematicsListPage canEdit={true} />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/thematic/:thematicId"
            element={
              <AllowAccessTo
                permissions={['all:channel']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <Thematic canEdit={true} />
              </AllowAccessTo>
            }
          />

          {/* ============================ ApiKey page ============================ */}
          <Route
            path="organizations/:organizationId/api-key"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                featureFlags={['API_KEY']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <ApiKeyHub />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/api-key/:apiKeyId"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                featureFlags={['API_KEY']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <ApiKey />
              </AllowAccessTo>
            }
          />

          {/* ============================ Notificatoins pages ============================ */}

          <Route
            path="organizations/:organizationId/notifications-hub"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                featureFlags={['NOTIFICATION']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <NotificationsHub />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/notifications-hub/:notificationId"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                featureFlags={['NOTIFICATION']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <Notification canEdit={true} />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/notifications-hub/:notificationId"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                featureFlags={['NOTIFICATION']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <Notification canEdit={false} />
              </AllowAccessTo>
            }
          />
          {/* ============================ Audit Operations pages ============================ */}

          <Route
            path="organizations/:organizationId/audit-operations"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <AuditOperationsHub />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/audit-operations/llm-requests/:llmRequestId"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <LlmRequestDetail />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/audit-operations/:auditOperationId"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <AuditOperationDetail />
              </AllowAccessTo>
            }
          />

          {/* ============================ Artifact pages ============================ */}

          <Route
            path="organizations/:organizationId/artifacts"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <ArtifactHub />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/artifacts/batch-create"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <ArtifactBatchCreate />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/artifacts/:artifactId"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <ArtifactDetail />
              </AllowAccessTo>
            }
          />
          {/* ============================ Connections pages ============================ */}

          <Route
            path="organizations/:organizationId/connections"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                featureFlags={['CONNECTION']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <Connections />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/connections/:connectionId"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                featureFlags={['CONNECTION']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <ConnectionDetail />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/connections/:connectionId/objects"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                featureFlags={['CONNECTION']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <ConnectionDetail />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/connections/:connectionId/objects/:objectId"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                featureFlags={['CONNECTION']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <ConnectionObjectDetail />
              </AllowAccessTo>
            }
          />

          {/* ============================ Google connectio pages ============================ */}
          <Route
            path={CONFIG.GOOGLE_DRIVE_REDIRECT_PATH}
            element={
              <AllowAccessTo permissions={['all:organization']}>
                <GoogleDriveConnectionPage canEdit={false} />
              </AllowAccessTo>
            }
          />

          {/* ============================ Zoho connection pages ============================ */}
          <Route
            path={CONFIG.ZOHO_REDIRECT_PATH}
            element={
              <AllowAccessTo permissions={['all:organization']}>
                <ZohoConnectionCallbackPage />
              </AllowAccessTo>
            }
          />

          {/* ============================ Document collection pages ============================ */}

          <Route
            path="organizations/:organizationId/document-collections"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                featureFlags={['DOCUMENT']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <DocumentCollectionHub />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/document-collections/:collectionId"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                featureFlags={['DOCUMENT']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <DocumentCollection />
              </AllowAccessTo>
            }
          />

          {/* ============================ Voice Profiles pages ============================ */}

          <Route
            path="organizations/:organizationId/voice-profiles"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                featureFlags={['VOICE']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <VoiceProfiles />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/voice-profiles/new"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                featureFlags={['VOICE']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <NewVoiceProfile />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/voice-profiles/:voiceProfileId"
            element={
              <AllowAccessTo
                permissions={['all:assistant']}
                featureFlags={['VOICE']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <VoiceProfileDetails />
              </AllowAccessTo>
            }
          />

          {/* ============================ Information Package pages ============================ */}

          <Route
            path="organizations/:organizationId/information-package"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                featureFlags={['INFORMATION_PACKAGE']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <InformationPackage />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/information-package/new-information-package"
            element={
              <AllowAccessTo
                permissions={['all:document']}
                featureFlags={['INFORMATION_PACKAGE']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <NewInformationPackage />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/information-package/:informationPackageId"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                featureFlags={['INFORMATION_PACKAGE']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <InformationPackageDetail />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/information-package/:informationPackageId/unit/:unitId"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                featureFlags={['INFORMATION_PACKAGE']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <InformationPackageUnit />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/information-package/:informationPackageId/unit/:unitId/artifact/:artifactItemId"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                featureFlags={['INFORMATION_PACKAGE']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <InformationPackageArtifact />
              </AllowAccessTo>
            }
          />

          {/* ============================ Information Package Collections pages ============================ */}
          <Route
            path="organizations/:organizationId/information-package-collections"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                featureFlags={['INFORMATION_PACKAGE']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <InformationPackageCollections />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/information-package-collections/:ipCollectionId"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                featureFlags={['INFORMATION_PACKAGE']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <InformationPackageCollection canEdit={true} />
              </AllowAccessTo>
            }
          />

          {/* ============================ Fragment Route pages ============================ */}
          <Route
            path="organizations/:organizationId/fragments"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <FragmentHub />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/fragments/:fragmentId"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <FragmentDetail />
              </AllowAccessTo>
            }
          />

          {/* ============================ Post-Call Analysis pages ============================ */}

          <Route
            path="organizations/:organizationId/post-call-analysis"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <PostCallAnalysisHub />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/post-call-analysis/:configId"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <PostCallAnalysisDetail />
              </AllowAccessTo>
            }
          />

          <Route
            path="organizations/:organizationId/post-call-analysis/:configId/analytics"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <PostCallAnalytics />
              </AllowAccessTo>
            }
          />

          {/* ============================ Record Route pages ============================ */}

          <Route
            path="organizations/:organizationId/record-route"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                featureFlags={['RECORD_ROUTE']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <RecordRouteHub />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/record-route/:recordRouteId"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                featureFlags={['RECORD_ROUTE']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <RecordRouteDetail />
              </AllowAccessTo>
            }
          />

          {/* ============================ Connection Object Map ============================ */}

          <Route
            path="organizations/:organizationId/connection-object-map"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <ConnectionsObjectMap />
              </AllowAccessTo>
            }
          />
          <Route
            path="organizations/:organizationId/connection-object-map/:connectionObjectMapId"
            element={
              <AllowAccessTo
                permissions={['all:organization']}
                redirectTo={`/organizations/${status.organization_id}`}
              >
                <ConnectionsObjectMapDetail />
              </AllowAccessTo>
            }
          />
        </Route>

        {/* Catch-all route for unknown paths */}
        <Route path="*" element={<NoMatch />} />
      </Routes>
    </BrowserRouter>
  );
};

export default Router;
